import { Kundetype, ProduktSpesifikasjon } from "codegen";
import { useMemo } from "react";

import { useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { capitalizeFirstLetterRestLower } from "utils/Utils";

interface ProductsProps {
	productOptionsTariffIds?: Set<string>;
	/** Bare produkter for denne kundetypen. Kundetypen står da ikke i etiketten, siden den er lik for alle. */
	kundetype?: Kundetype;
	/** Bare hovedprodukter, ikke tilleggsprodukter. */
	mainProductsOnly?: boolean;
}

export enum CustomerType {
	Private = "Privat",
	Company = "Bedrift",
}

export function mapCustomerType(type?: Kundetype) {
	if (type === Kundetype.Bedrift) {
		return CustomerType.Company;
	}
	return CustomerType.Private;
}

export default function useProducts({ productOptionsTariffIds, kundetype, mainProductsOnly }: ProductsProps = {}) {
	const {
		data: products,
		isLoading: isLoadingProducts,
		isRefetching: isRefetchingProducts,
		error: productsError,
	} = useStrombestillingApiQuery<ProduktSpesifikasjon[]>("/produkter/spesifikasjoner?alle=true", ["products", "specifications", "tariffId"]);

	const productSelectOptions = useMemo(() => {
		if (!products || (!productOptionsTariffIds && !kundetype)) return [];
		const options = products
			.filter((product) => product.navn !== undefined)
			.filter((p) => !productOptionsTariffIds || productOptionsTariffIds.has(p.tariffId!))
			.filter((p) => !kundetype || p.kundetype === kundetype)
			.filter((p) => !mainProductsOnly || p.hovedprodukt)
			.map((product) => ({
				...product,
				value: product.tariffId ?? "",
				label: kundetype ? product.navn! : `${product.navn} (${capitalizeFirstLetterRestLower(mapCustomerType(product.kundetype))})`,
			}));
		return kundetype ? options.sort((a, b) => a.label.localeCompare(b.label, "nb")) : options.sort((a, b) => b.kundetype!.localeCompare(a.kundetype!));
	}, [products, productOptionsTariffIds, kundetype, mainProductsOnly]);

	return {
		products,
		productSelectOptions,
		isLoadingProducts,
		isRefetchingProducts,
		productsError,
	};
}
